import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import FloorPlanViewer from './FloorPlanViewer';
import type { FloorPlanResponse } from '@/features/booking/types/floorPlanTypes';
import { env } from '@/app/config/environment';

interface FloorPlanCarouselProps {
  floorPlans: FloorPlanResponse[];
  availableTables: string[];
  selectedTable: string | null;
  onTableSelect: (tableNumber: string) => void;
  onFloorPlanChange?: (floorPlan: FloorPlanResponse) => void;
  pricePerGuest?: number;
  currency?: string;
  inCartTables?: string[];
  localTables?: string[];
  soldOutCartTables?: string[];
}

export interface FloorPlanCarouselRef {
  goToIndex: (index: number) => void;
}

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

const FloorPlanCarousel = forwardRef<FloorPlanCarouselRef, FloorPlanCarouselProps>(({
  floorPlans,
  availableTables,
  selectedTable,
  onTableSelect,
  onFloorPlanChange,
  pricePerGuest,
  currency,
  inCartTables = [],
  localTables = [],
  soldOutCartTables = [],
}, ref) => {
  const [[page, direction], setPage] = useState([0, 0]);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // We only have one active index, which is page modulo length
  // However, for framer motion usually it's better to just track index if we don't need infinite paging logic with same keys
  // Let's stick to simple index for now but track direction
  const activeIndex = page;

  // Expose goToIndex to parent via ref
  useImperativeHandle(ref, () => ({
    goToIndex: (index: number) => {
      if (index >= 0 && index < floorPlans.length) {
        setPage([index, index > page ? 1 : -1]);
      }
    },
  }), [floorPlans.length, page]);

  // Measure container width
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Clamp activeIndex if floor plans change
  useEffect(() => {
    if (page >= floorPlans.length) {
      setPage([Math.max(0, floorPlans.length - 1), 0]);
    }
  }, [floorPlans.length, page]);

  // Notify parent when active floor plan changes
  useEffect(() => {
    if (floorPlans.length > 0 && floorPlans[activeIndex]) {
      onFloorPlanChange?.(floorPlans[activeIndex]);
    }
  }, [activeIndex, floorPlans, onFloorPlanChange]);

  // Preload images for adjacent floor plans
  useEffect(() => {
    if (floorPlans.length <= 1) return;
    
    const imageIdsToPreload = new Set<string>();
    
    // Preload next 2
    for (let i = 1; i <= 2; i++) {
        const nextIndex = (activeIndex + i) % floorPlans.length;
        const nextFloorPlan = floorPlans[nextIndex];
        if (nextFloorPlan?.image_id) {
             imageIdsToPreload.add(nextFloorPlan.image_id);
        }
    }
    
    // Also preload previous 1
    const prevIndex = (activeIndex - 1 + floorPlans.length) % floorPlans.length;
    if (floorPlans[prevIndex]?.image_id) {
         imageIdsToPreload.add(floorPlans[prevIndex].image_id);
    }

    imageIdsToPreload.forEach(id => {
         const img = new window.Image();
         img.src = `${env.MAIN_GCS_URL}/${id}`;
    });
  }, [activeIndex, floorPlans]);

  const paginate = (newDirection: number) => {
    let newIndex = page + newDirection;
    if (newIndex < 0) newIndex = floorPlans.length - 1;
    if (newIndex >= floorPlans.length) newIndex = 0;
    setPage([newIndex, newDirection]);
  };

  if (floorPlans.length === 0) return null;

  const currentFloorPlan = floorPlans[activeIndex];

  const handleTouchStart = (e: React.TouchEvent) => {
    // Only capture single touch
    if (e.touches.length > 1) return;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current || floorPlans.length <= 1) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;

    const dx = touchEndX - touchStartRef.current.x;
    const dy = touchEndY - touchStartRef.current.y;

    // Horizontal swipe threshold
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      if (dx > 0) {
        paginate(-1); // swipe right
      } else {
        paginate(1); // swipe left
      }
    }
    
    touchStartRef.current = null;
  };

  return (
    <div className="space-y-4">
      {/* Floor plan name and arrows - Top */}
      <div className="flex justify-between items-center relative h-10 px-2 lg:px-4">
        {floorPlans.length > 1 ? (
          <button
            onClick={() => paginate(-1)}
            className="z-20 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white transition-all transform hover:scale-105"
            aria-label="Previous floor plan"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        ) : (
          <div className="w-10 h-10" />
        )}

        <h3 className="text-lg font-semibold text-white text-center flex-1 mx-2 truncate">{currentFloorPlan?.name}</h3>
        
        {floorPlans.length > 1 ? (
          <button
            onClick={() => paginate(1)}
            className="z-20 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white transition-all transform hover:scale-105"
            aria-label="Next floor plan"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
           <div className="w-10 h-10" />
        )}
      </div>

      <div className="relative group">

        {/* Canvas container with swipe support */}
        <div 
          className="relative w-full rounded-xl overflow-hidden border border-gray-700 bg-gray-900 aspect-[3/2]" 
          style={{ touchAction: 'pan-y' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            ref={containerRef}
            className="w-full h-full relative"
          >
          <AnimatePresence initial={false} custom={direction} mode='popLayout'>
            <motion.div
              key={page}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
            >
              {containerWidth > 0 && currentFloorPlan && (
                <FloorPlanViewer
                  floorPlan={currentFloorPlan}
                  availableTables={availableTables}
                  selectedTable={selectedTable}
                  onTableSelect={onTableSelect}
                  containerWidth={containerWidth}
                  pricePerGuest={pricePerGuest}
                  currency={currency}
                  inCartTables={inCartTables}
                  localTables={localTables}
                  soldOutCartTables={soldOutCartTables}
                />
              )}
            </motion.div>
          </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Dot indicators */}
      {floorPlans.length > 1 && (
        <div className="flex justify-center gap-2">
          {floorPlans.map((fp, idx) => (
            <button
              key={fp.id}
              onClick={() => {
                const newDirection = idx > page ? 1 : -1;
                setPage([idx, newDirection]);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                idx === activeIndex
                  ? 'bg-blue-500 w-4'
                  : 'bg-gray-600 hover:bg-gray-500'
              }`}
              aria-label={`Go to floor plan ${fp.name}`}
            />
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs font-medium px-4 pt-4 pb-2">
        <div className="flex items-center gap-1.5 opacity-90">
          <div className="w-3 h-3 rounded-[3px] bg-blue-500/50 border border-blue-500/80 shadow-[0_0_8px_rgba(59,130,246,0.3)]"></div>
          <span className="text-gray-300 tracking-wide">In Cart</span>
        </div>
        <div className="flex items-center gap-1.5 opacity-90">
          <div className="w-3 h-3 rounded-[3px] bg-yellow-500/50 border border-yellow-500/80 shadow-[0_0_8px_rgba(234,179,8,0.3)]"></div>
          <span className="text-gray-300 tracking-wide">Pending</span>
        </div>
        <div className="flex items-center gap-1.5 opacity-90">
          <div className="w-3 h-3 rounded-[3px] bg-green-500/30 border border-green-500/50"></div>
          <span className="text-gray-300 tracking-wide">Available</span>
        </div>
        <div className="flex items-center gap-1.5 opacity-90">
          <div className="w-3 h-3 rounded-[3px] bg-red-500/20 border border-red-500/40"></div>
          <span className="text-gray-300 tracking-wide">Unavailable</span>
        </div>
        {soldOutCartTables.length > 0 && (
          <div className="flex items-center gap-1.5 opacity-90">
            <div className="w-3 h-3 rounded-[3px] bg-red-500/40 border border-red-500/70 shadow-[0_0_8px_rgba(239,68,68,0.3)]"></div>
            <span className="text-gray-300 tracking-wide">In Cart · Sold Out</span>
          </div>
        )}
      </div>
    </div>
  );
});

FloorPlanCarousel.displayName = 'FloorPlanCarousel';

export default FloorPlanCarousel;
